<template>
    <BaseTemplate :page-title="''" :app-name="'CCDTI'">
      <template #content>
        <div class="p-4 space-y-6">
          <!-- Welcome Section -->
          <div class="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
            <div class="flex items-center space-x-4">
              <div class="p-3 rounded-full" :style="{ backgroundColor: 'var(--clinic-btn-bg-hover)' }">
                <Home class="h-6 w-6" :style="{ color: 'var(--clinic-icon-color)' }" />
              </div>
              <div>
                <h2 class="text-2xl font-bold text-gray-800 dark:text-white">Bem-vindo(a), {{ firstName }}!</h2>
                <p class="text-gray-500 dark:text-gray-400">{{ getCurrentDateMessage() }}</p>
              </div>
              
            </div>
          </div>
  
          <!-- Modules Grid -->
          <div class="hidden md:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            <div v-for="(module, index) in modules" :key="index" 
                 @click="navigateTo(module.route)"
                 class="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 transition-all duration-300 hover:shadow-lg hover:translate-y-[-5px] cursor-pointer">
              <div class="flex flex-col items-center text-center space-y-4">
                <div class="p-4 rounded-full" :style="{ backgroundColor: 'var(--clinic-btn-bg-hover)' }">
                  <component :is="module.icon" class="h-8 w-8" :style="{ color: 'var(--clinic-icon-color)' }" />
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-gray-800 dark:text-white">{{ module.title }}</h3>
                  <p class="text-sm text-gray-500 dark:text-gray-400 mt-1" :style="{ color: 'var(--clinic-font-color)' }">{{ module.description }}</p>
                </div>
                <div class="mt-2 text-sm font-medium flex items-center" :style="{ color: 'var(--clinic-font-color)' }">
                  <span>Acessar</span>
                  <ArrowRight class="h-4 w-4 ml-1" :style="{ color: 'var(--clinic-font-color)' }" />
                </div>
              </div>
            </div>
          </div>
  
          <!-- Modules Carousel for Mobile -->
    <!-- Modules Carousel for Mobile -->
    <div class="md:hidden px-4 py-4">
    <div class="grid grid-cols-2 gap-4">
        <div v-for="(module, index) in modules" :key="index"
            @click="navigateTo(module.route)"
            class="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-4 flex flex-col items-center justify-between h-36 transition-transform hover:scale-105 cursor-pointer">
        <div class="p-3 rounded-full mb-2" :style="{ backgroundColor: 'var(--clinic-btn-bg-hover)' }">
            <component :is="module.icon" class="h-6 w-6" :style="{ color: 'var(--clinic-icon-color)' }" />
        </div>
        <div class="text-center">
            <h3 class="text-sm font-semibold text-gray-800 dark:text-white leading-tight" :style="{ color: 'var(--clinic-font-color)' }">{{ module.title }}</h3>
            <p class="text-xs text-gray-500 dark:text-gray-400 mt-1" :style="{ color: 'var(--clinic-font-color)' }">{{ module.description }}</p>
        </div>
        </div>
    </div>
    </div>

          <!-- Quick Stats -->
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div v-for="(stat, index) in stats" :key="index" 
                 class="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 border-l-4" 
                 :class="stat.borderColor">
              <div class="flex justify-between items-center">
                <div>
                  <p class="text-sm font-medium text-gray-500 dark:text-gray-400" :style="{ color: 'var(--clinic-font-color)' }">{{ stat.title }}</p>
                  <h3 class="text-2xl font-bold text-gray-800 dark:text-white mt-1" :style="{ color: 'var(--clinic-font-color)' }">{{ stat.value }}</h3>
                </div>
                <div class="p-3 rounded-full" :class="stat.bgColor">
                  <component :is="stat.icon" class="h-6 w-6" :style="{ color: 'var(--clinic-icon-color)' }" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </BaseTemplate>


  </template>
  
  <script setup>
  import { onMounted, computed } from 'vue';
  import { useRouter } from 'vue-router';
  import { 
    Home, 
    CalendarPlus2,
    Settings,
    UserPlus,
    Stethoscope,
    ClipboardMinus,
    ArrowRight,
    ScrollText,
    Send,
    DollarSign,
    Package,
    Globe,
    PhoneCall,
    Edit
  } from 'lucide-vue-next';
  
  import BaseTemplate from '@/views/templates/BaseTemplate.vue'; // Importando o template base
import { useUserStore } from '@/stores/userStore';
import { usePermissionStore } from '@/stores/permissionStore';

  import 'swiper/swiper-bundle.css';
  
  const router = useRouter();
  
  // Dados do usuário

const userStore = useUserStore();
const permissionStore = usePermissionStore();
const firstName = computed(() => userStore.nome || 'Usuário');

  
  // Função para obter mensagem baseada na hora do dia
  const getCurrentDateMessage = () => {
    const now = new Date();
    const hour = now.getHours();
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    const dateStr = now.toLocaleDateString('pt-BR', options);
    
    let greeting = '';
    if (hour < 12) greeting = 'Bom dia';
    else if (hour < 18) greeting = 'Boa tarde';
    else greeting = 'Boa noite';
    
    return `${greeting}! Hoje é ${dateStr}`;
  };
  
  
  const allModules = [
    {
      title: 'Agendas',
      description: 'Gerenciar agendamentos',
      icon: CalendarPlus2,
      route: '/schedule',
      requiredPermission: 'can_access_schedule',
    },
      {
      title: 'Pacientes',
      description: 'Gerenciar o cadastro de pacientes',
      icon: UserPlus,
      route: '/patients',
      requiredPermission: 'can_access_patients',
    },
    {
      title: 'Atendimento',
      description: 'Gerenciar o atendimento',
      icon: Stethoscope,
      route: '/atendimento',
      requiredPermission: 'can_access_atendimento',
    },
     {
      title: 'Laudos',
      description: 'Gerencie os laudos emitidos',
      icon: ClipboardMinus,
      route: '/medicalreports',
      requiredPermission: 'can_access_medicalreports',
    },
    {
      title: 'Liberar Exames',
      description: 'Gerenciar a liberação de exames',
      icon: Globe,
      route: '/exam-release',
      requiredPermission: 'can_access_exam_release',
    },
    {
      title: 'Editar Exames',
      description: 'Gerenciar a edição de exames',
      icon: Edit,
      route: '/editexam',
      requiredPermission: 'can_access_edit_exam',
    },
    {
      title: 'Entrega de Exames',
      description: 'Gerenciar a entrega de exames',
      icon: Send,
      route: '/entrega-exames',
      requiredPermission: 'can_access_exam_delivery',
    },

    {
      title: 'Fila de Reconvocação',
      description: 'Gerenciar a fila de convocação',
      icon: PhoneCall,
      route: '/recall-queue',
      requiredPermission: 'can_access_recall_queue',
    },
    {
      title: 'Relatórios',
      description: 'Gerenciar os relatórios',
      icon: ScrollText,
      route: '/reports',
      requiredPermission: 'can_access_reports',
    },
    {
      title: 'Faturamento',
      description: 'Gerenciar o faturamento',
      icon: DollarSign,
      route: '/faturamento',
      requiredPermission: 'Faturamento.can_access_faturamento',
      
    },
    {
      title: 'Estoque',
      description: 'Gerenciar materiais e fornecedores',
      icon: Package,
      route: '/estoque',
      requiredPermission: 'Estoque.can_access_estoque',
    },
    {
      title: 'Configurações',
      description: 'Configurações do Sistema',
      icon: Settings,
      route: '/config',
      requiredPermission: 'can_access_config',
    },

  ];
  
  const modules = computed(() => 
    allModules.filter(module => {
      if (module.requiredPermission) {
        return permissionStore.hasPermission(module.requiredPermission);
      }
      return true;
    })
  );
  
  // Função para navegação
  const navigateTo = (route) => {
    router.push(route);
  };
  
  onMounted(() => {
    // Qualquer lógica de inicialização pode ser adicionada aqui
  });
  </script>
  
  <style scoped>
  /* Transições suaves para hover */
  .hover\:translate-y-\[-5px\]:hover {
    transform: translateY(-5px);
  }
  </style>